/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import { Spacer, Heading, theme, Text, Label } from "@nordcloud/gnui";
import { FormGroup, TransformableInput } from "~/components";
import { StandardForm } from "../components/StandardForm";
import { AccountContacts, AccountParams } from "../types";
import { CopyBox } from "./components/CopyBox";
import { StepContainer } from "./components/StepContainer";
import {
  AZURE_COMMAND,
  URL_AZURE,
  URL_AZURE_SUBSCRIPTION_ID,
} from "./constants";
import { trimTransform } from "./utils";
import { schemaCredsAzure } from "./validators";

type Props = {
  onAddAccountSubmit: SubmitHandler<FormData>;
  accountParams: AccountParams;
  additionalSettings: AccountContacts;
  setAdditionalSettings: (settings: AccountContacts) => void;
};

enum FormField {
  OWNER_ID = "ownerId",
  CONTACT_IDS = "contactIds",
  PROVIDER_ID = "providerId",
  APP_ID = "appId",
  PASSWORD = "password",
  TENANT_ID = "tenantId",
}

export function AzureAccountForm({
  onAddAccountSubmit,
  accountParams,
  additionalSettings,
  setAdditionalSettings,
}: Props) {
  const formMethods = useForm({
    resolver: yupResolver(schemaCredsAzure),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods;

  React.useEffect(() => {
    register(FormField.OWNER_ID);
    register(FormField.CONTACT_IDS);
  }, [register]);

  return (
    <FormProvider {...formMethods}>
      <form id="AZURE-cred-form" onSubmit={handleSubmit(onAddAccountSubmit)}>
        <Spacer height={theme.spacing.spacing07} />
        <StandardForm
          accountParams={accountParams}
          additionalSettings={additionalSettings}
          setAdditionalSettings={setAdditionalSettings}
        />

        <When condition={Boolean(accountParams.provider)}>
          <Spacer height={theme.spacing.spacing04} />
          <Heading level={3} marginBottom="spacing03">
            Account Credentials
          </Heading>
          <Text color={theme.color.text.text02}>
            To on-board a subscription you need to create a Service Principal.
          </Text>
        </When>

        <StepContainer counter={1}>
          <Text>
            Sign in to the{" "}
            <a href={URL_AZURE} target="_blank" rel="noreferrer">
              Microsoft Azure
            </a>
          </Text>
          <FormGroup error={errors[FormField.PROVIDER_ID]}>
            <Label
              required
              htmlFor={FormField.PROVIDER_ID}
              name="Provide Subscription ID"
            />
            <TransformableInput
              name={FormField.PROVIDER_ID}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              defaultValue={accountParams.providerId}
              placeholder="e.g. 291bba3f-e0a5-47bc-a099-3bdcb2a50a05"
              id={FormField.PROVIDER_ID}
            />
          </FormGroup>
          <a href={URL_AZURE_SUBSCRIPTION_ID} target="_blank" rel="noreferrer">
            More information about Subscription ID
          </a>
        </StepContainer>
        <StepContainer counter={2}>
          <Text>Run Azure Cloud Shell and execute the following command:</Text>
          <CopyBox>{AZURE_COMMAND}</CopyBox>
        </StepContainer>
        <StepContainer counter={3}>
          <Text>
            The output will include credentials that you should provide below
          </Text>
          <FormGroup error={errors[FormField.APP_ID]}>
            <Label required htmlFor={FormField.APP_ID} name="App ID" />
            <TransformableInput
              name={FormField.APP_ID}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              placeholder="e.g. a78c76bb-8cae-4db4-a45a-c1420613e01b"
              id={FormField.APP_ID}
            />
          </FormGroup>
          <FormGroup error={errors[FormField.PASSWORD]}>
            <Label required htmlFor={FormField.PASSWORD} name="Password" />
            <TransformableInput
              name={FormField.PASSWORD}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              type="password"
              id={FormField.PASSWORD}
            />
          </FormGroup>
          <FormGroup error={errors[FormField.TENANT_ID]}>
            <Label required htmlFor={FormField.TENANT_ID} name="Tenant" />
            <TransformableInput
              name={FormField.TENANT_ID}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              placeholder="e.g. 6ed9f903-f1be-44e3-9fef-e7e0e3f48564"
              id={FormField.TENANT_ID}
            />
          </FormGroup>
        </StepContainer>
        <StepContainer counter={4}>
          <Text marginBottom={theme.spacing.spacing01}>
            Add the created Service Principal to your subscription with{" "}
            <span css={{ fontWeight: theme.fontWeights.medium }}>Reader</span>{" "}
            role.
          </Text>
        </StepContainer>
      </form>
    </FormProvider>
  );
}
